/**
 * Regra ÚNICA de dado sensível pelo nome do campo (review r14: visual M1 e M2, spec M2). Antes havia quatro regras que
 * divergiam (`mascaraDoCampo` e `extraSensivelPorNome` no formulário, `censura-json` e `rotulos`), todas ancoradas no fim da
 * chave: o Editar mostrava cru o que a ficha escondia, e `nascimento_socio`, `cep_entrega` ou `valor_contrato` passavam crus.
 *
 * Quem usa: o Editar (`mascaraDoCampo` e o `CampoData`/número do `CampoControlado`), a ficha e a Tabela (`valorExtra`), a
 * Auditoria e o histórico do chatter (`censura-json` e `rotuloValor`).
 *
 * Como lê a chave: tira o `extras.`, quebra em termos por `_`, `.`, `-`, espaço e camelCase (`contaCorrente`, `numeroCPF`),
 * minúsculas e sem acento. O termo vale em QUALQUER posição (`nascimento_socio`, `cep_entrega`, `chave_pix_socio`,
 * `clienteCpf`). Termo é palavra inteira: `org` não é `rg`, `cargo` não é `rg`, `contato` não é `conta`.
 *
 * Exceções (o nome diz que não é o dado em si):
 *  - texto livre como PRIMEIRO termo (review r15 visual M1; descrição, observação, título, motivo, nota, assunto, mensagem,
 *    corpo, conteúdo, comentário, justificativa):
 *    vira texto livre e passa pela censura de padrões (`textoLivre`). `titulo_eleitor` e `nota_fiscal` não são texto livre;
 *  - identificador ou metadado: último termo `id`, `ids`, `uuid`, `url`, `slug`, `tipo`, `status`, `formato`, `mascara`,
 *    `rotulo`, `label`, `em` (instante: `email_enviado_em`, review r15 spec O2); primeiro termo `tipo`, `categoria`, `modelo`, `formato`, `situacao`, `status` ou um verbo de opção
 *    (`possui`, `tem`, `usa`, `exige`, `aceita`, `permite`, `is`, `has`): `tipo_documento`, `conta_id`, `tem_pix`;
 *  - cidade, UF, município, país e estado ficam à vista (como no endereço formatado): `endereco_cidade`, `cep_uf`;
 *  - dinheiro: `total` só no começo; quantidade, percentual e contagem não são dinheiro (`quantidade_total`,
 *    `percentual_desconto`, `total_itens`); `centro_custo` é o nome do centro, `tabela_preco` é o nome da tabela;
 *  - conta: `conta_contabil` e `plano_conta` são do plano de contas, não conta bancária;
 *  - número da casa: `numero` só conta ao lado de um termo de endereço (`numero_endereco`, `endereco.numero`, `numero_casa`)
 *    ou quando quem chama diz que está num endereço (`emEndereco`). `numero_pedido`, `numero_nota` não são;
 *  - nome de pessoa: `razao_social`, `nome_fantasia`, `nome_completo`, `titular` e `nome` junto de um papel de pessoa
 *    (`nome_socio`, `cliente_nome`, `nome_conjuge`). O `nome` solto depende do registro (decide quem chama).
 *
 * A tabela de testes é `apps/web/tests/r14-sensivel-por-nome.test.ts`.
 */

export type TipoSensivel =
  | 'dinheiro'
  | 'cpf'
  | 'cnpj'
  | 'documento'
  | 'rg'
  | 'pis'
  | 'titulo_eleitor'
  | 'passaporte'
  | 'conta'
  | 'agencia'
  | 'pix'
  | 'email'
  | 'telefone'
  | 'endereco'
  | 'cep'
  | 'bairro'
  | 'logradouro'
  | 'numero_casa'
  | 'nascimento'
  | 'nome';

/** Tipos do grupo de documento (todos passam pelo formatador de documento). */
export const TIPOS_DOCUMENTO: ReadonlySet<TipoSensivel> = new Set(['cpf', 'cnpj', 'documento', 'rg', 'pis', 'titulo_eleitor', 'passaporte']);
/** Tipos que somem inteiros (sem formatador que mostre parte): endereço e suas partes, agência, nascimento, dinheiro. */
export const TIPOS_OCULTOS: ReadonlySet<TipoSensivel> = new Set(['endereco', 'cep', 'bairro', 'logradouro', 'numero_casa', 'agencia', 'nascimento', 'dinheiro']);

/** Tamanho máximo da chave lida pela regra (review r15 robustez M1): nenhuma chave real passa de algumas dezenas. */
export const CHAVE_MAX = 200;

const ehMaiuscula = (c: string) => c >= 'A' && c <= 'Z';
const ehMinuscula = (c: string) => c >= 'a' && c <= 'z';
const ehDigito = (c: string) => c >= '0' && c <= '9';

/**
 * Quebra a chave em termos: `extras.chave_pix_socio` vira [chave, pix, socio]; `contaCorrente`, [conta, corrente];
 * `numeroCPF`, [numero, cpf]; `XMLHttp`, [xml, http]; `salario2`, [salario, 2] (review r15 spec M1: a chave do campo
 * personalizado aceita dígito, e letra colada em dígito separa).
 *
 * Review r15 robustez M1: a versão com `replace(/([A-Z]+)([A-Z][a-z])/g)` era quadrática numa chave longa só de maiúsculas
 * (60 mil "A" em 5,9 s; qualquer chave de API grava uma chave assim no log do MCP e a aba do administrador travava no modo
 * discreto). Agora a chave é cortada em `CHAVE_MAX` caracteres antes de tudo e a quebra é um laço linear, sem regex com
 * retrocesso: separa em todo caractere fora de [A-Za-z0-9] (depois de tirar o acento), antes da maiúscula que segue minúscula
 * ou dígito, antes da última maiúscula de uma sigla seguida de minúscula e antes do dígito que segue letra.
 */
export function termosDaChave(chave: string): string[] {
  const s = chave
    .replace(/^extras\./, '')
    .slice(0, CHAVE_MAX)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  const termos: string[] = [];
  let atual = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (!ehMaiuscula(c) && !ehMinuscula(c) && !ehDigito(c)) {
      if (atual) termos.push(atual);
      atual = '';
      continue;
    }
    if (atual) {
      const ant = s[i - 1]!;
      const prox = s[i + 1] ?? '';
      const quebra =
        (ehMaiuscula(c) && (ehMinuscula(ant) || ehDigito(ant))) ||
        (ehMaiuscula(c) && ehMaiuscula(ant) && ehMinuscula(prox)) ||
        (ehDigito(c) && (ehMinuscula(ant) || ehMaiuscula(ant)));
      if (quebra) {
        termos.push(atual);
        atual = '';
      }
    }
    atual += c.toLowerCase();
  }
  if (atual) termos.push(atual);
  return termos;
}

const LIVRE = new Set(['descricao', 'observacao', 'observacoes', 'obs', 'titulo', 'motivo', 'nota', 'notas', 'assunto', 'mensagem', 'corpo', 'conteudo', 'comentario', 'comentarios', 'justificativa', 'description', 'notes', 'comment', 'message']);
// Review r15 spec O2: `_em` é o instante de um evento (`email_enviado_em`, `pago_em`), não o dado.
const ULTIMO_META = new Set(['id', 'ids', 'uuid', 'url', 'slug', 'tipo', 'tipos', 'status', 'formato', 'mascara', 'rotulo', 'label', 'em']);
const PRIMEIRO_META = new Set(['tipo', 'categoria', 'modelo', 'formato', 'situacao', 'status', 'possui', 'tem', 'usa', 'exige', 'aceita', 'permite', 'is', 'has']);
const LOCAL_PUBLICO = new Set(['cidade', 'uf', 'municipio', 'pais', 'estado', 'city', 'state', 'country']);

/**
 * Dinheiro pelo nome. Review r15 spec M1: sinônimos comuns que um administrador usaria num campo personalizado (orçamento,
 * mensalidade, comissão, bônus, aluguel, crédito, capital, patrimônio, ticket, honorário, pagamento, receita, despesa, dívida,
 * prêmio, investimento, fatura, mensal), em pt, en e es. A lista nunca fecha: o campo personalizado tem a marca `sensivel` na
 * definição e o número e a data somem pelo tipo (`sensivelDoExtra`).
 */
const DINHEIRO = new Set([
  // pt
  'valor', 'valores', 'preco', 'precos', 'salario', 'salarios', 'remuneracao', 'saldo', 'juros', 'multa', 'desconto', 'custo', 'centavos', 'renda',
  'honorario', 'honorarios', 'faturamento', 'orcamento', 'orcamentos', 'mensalidade', 'mensalidades', 'comissao', 'comissoes', 'bonus', 'aluguel',
  'alugueis', 'credito', 'creditos', 'capital', 'patrimonio', 'ticket', 'pagamento', 'pagamentos', 'receita', 'receitas', 'despesa', 'despesas',
  'divida', 'dividas', 'premio', 'premios', 'investimento', 'investimentos', 'fatura', 'faturas', 'mensal', 'montante', 'prolabore', 'vale',
  'taxa', 'tarifa', 'limite', 'gratificacao', 'adiantamento', 'reembolso', 'faturado',
  // en
  'amount', 'price', 'cost', 'balance', 'salary', 'wage', 'income', 'budget', 'fee', 'fees', 'commission', 'rent', 'credit', 'equity', 'payment',
  'payments', 'revenue', 'expense', 'expenses', 'debt', 'prize', 'investment', 'invoice', 'monthly', 'bonus', 'wages', 'pay', 'payroll',
  'allowance', 'reimbursement',
  // es
  'precio', 'costo', 'coste', 'sueldo', 'importe', 'monto', 'presupuesto', 'mensualidad', 'comision', 'bono', 'alquiler', 'arriendo', 'pago',
  'pagos', 'ingreso', 'ingresos', 'gasto', 'gastos', 'deuda', 'inversion', 'factura', 'salario', 'tarifa', 'cuota', 'honorarios', 'aguinaldo',
]);
/**
 * Termos que tiram o dinheiro: quantidade, percentual e contagem; e (review r15) data, número, código e forma do pagamento,
 * que dizem quando ou como, não quanto (`data_pagamento`, `numero_fatura`, `forma_pagamento`, `codigo_orcamento`).
 */
const NAO_DINHEIRO = new Set([
  'quantidade', 'qtd', 'qtde', 'percentual', 'porcentagem', 'percent', 'pct', 'itens', 'dias', 'horas', 'meses', 'anos', 'vezes', 'parcelas',
  'contagem', 'count', 'data', 'date', 'numero', 'num', 'nro', 'number', 'codigo', 'code', 'forma', 'meio', 'metodo', 'method', 'condicao', 'prazo',
  'frequencia', 'periodicidade',
]);

// Review r15 visual M2: os equivalentes em en e es (`direccion`, `domicilio`, `calle`, `postcode`, `colonia`, `barrio`).
const ENDERECO = new Set(['endereco', 'enderecos', 'address', 'complemento', 'direccion', 'domicilio', 'residencia']);
const LOGRADOURO = new Set(['logradouro', 'rua', 'street', 'calle', 'avenida']);
const CEP = new Set(['cep', 'zip', 'zipcode', 'postal', 'postcode']);
const BAIRRO = new Set(['bairro', 'neighborhood', 'barrio', 'colonia']);
const CONTEXTO_CASA = new Set(['endereco', 'casa', 'residencia', 'logradouro', 'rua', 'imovel', 'address', 'street']);
const NUMERO = new Set(['numero', 'num', 'nro', 'number']);
// Lista de 02/11 (CRM, item 1): o sexo do contato é dado pessoal; some inteiro no modo discreto, como o nascimento.
// O `tratamento` (Sr./Sra.) é derivado 1:1 do sexo e grava junto no histórico e na auditoria; vai junto (rl0211-1 SPEC-M3).
const SEXO = new Set(['sexo', 'tratamento']);
const NASCIMENTO = new Set(['nascimento', 'nasc', 'birth', 'birthdate', 'birthday', 'aniversario', 'dob', 'nacimiento', 'cumpleanos']);
const TELEFONE = new Set(['telefone', 'telefones', 'celular', 'whatsapp', 'whats', 'zap', 'fone', 'phone', 'mobile', 'tel', 'ramal', 'cel', 'telefono', 'movil', 'cell']);
// Review r15 spec M1: `ie` (inscrição estadual) também.
// Review r15 visual M2: documentos em en e es (DNI, SSN, NIF, NIE, cédula, RUT, CUIT, CURP, RFC).
const DOCUMENTO = new Set(['documento', 'cnh', 'rne', 'crnm', 'ctps', 'reservista', 'ie', 'dni', 'ssn', 'nif', 'nie', 'cedula', 'rut', 'cuit', 'curp', 'rfc', 'document']);
const PIS = new Set(['pis', 'nis', 'pasep', 'nit']);
const PAPEL_PESSOA = new Set(['socio', 'socia', 'conjuge', 'mae', 'pai', 'cliente', 'parceiro', 'fornecedor', 'funcionario', 'contato', 'titular', 'gestor', 'solicitante', 'dependente', 'filho', 'filha', 'avalista', 'fiador', 'representante', 'testemunha', 'beneficiario', 'completo', 'pessoa']);

/** Papel de pessoa que, sozinho na chave, é o nome dela (review r15 spec M1). `contato` e `cliente` ficam de fora (ambíguos). */
const PAPEL_SOZINHO = new Set(['mae', 'pai', 'conjuge', 'esposa', 'esposo', 'marido', 'filho', 'filha', 'socio', 'socia', 'avalista', 'fiador', 'fiadora', 'testemunha', 'dependente', 'beneficiario', 'representante']);

export interface OpcoesSensivel {
  /** A chave está dentro de um endereço (objeto `endereco`, entidade de endereço, campos de endereço ao lado). */
  emEndereco?: boolean;
  /**
   * Review r21 spec (M2): a chave é do protocolo de um conector de WhatsApp (payload da Meta ou do gateway não oficial, em
   * Integrações > Webhooks > Entrada). Ali `de`, `numero`, `from`, `wa_id`, `jid`, `recipient_id` são o número do contato ou
   * da empresa, e `name` (o `profile.name` da Meta, o nome de um contato compartilhado) é nome de pessoa. Fora desse contexto,
   * `de` e `para` são a mudança de um estado (auditoria), e `numero`, o de um pedido ou nota.
   */
  whatsapp?: boolean;
}

/** Review r21 spec (M2): chaves do protocolo dos conectores de WhatsApp que são o número de telefone (termos unidos por `_`). */
const TELEFONE_WHATSAPP = new Set(['de', 'para', 'numero', 'from', 'to', 'wa_id', 'jid', 'remote_jid', 'recipient_id', 'participant']);

/**
 * Singular de um termo (review r15 visual M2: `contas_bancarias`, `agencias`, `cpfs`, `documentos` casam como o singular):
 * `-oes`/`-aes` viram `-ao`, `-eis` vira `-el`, `-ais` vira `-al`, `-ns` vira `-m`, `-res`/`-zes`/`-ses` perdem o `-es` e o
 * resto perde o `s` final. Termo de até 3 letras fica como está (`pis`, `nis`, `gas`).
 */
export function singularDoTermo(x: string): string {
  if (x.length <= 3 || !x.endsWith('s')) return x;
  if (x.endsWith('oes') || x.endsWith('aes')) return `${x.slice(0, -3)}ao`;
  if (x.endsWith('eis')) return `${x.slice(0, -3)}el`;
  if (x.endsWith('ais')) return `${x.slice(0, -3)}al`;
  if (x.endsWith('ns')) return `${x.slice(0, -2)}m`;
  if (/[rzs]es$/.test(x)) return x.slice(0, -2);
  return x.slice(0, -1);
}

/** Contexto que faz de `end` a abreviação de endereço (`end_entrega`), e não o "fim" do inglês (`end_date`). */
const CONTEXTO_END = new Set(['entrega', 'cobranca', 'residencial', 'comercial', 'correspondencia', 'obra', 'fiscal', 'cliente', 'socio']);

/**
 * O tipo de dado sensível que o NOME do campo indica, ou null quando o nome não diz nada (segue a regra do tipo do campo).
 * Pura e barata (sem regex com retrocesso): quebra a chave e consulta conjuntos. Cada termo vale também no singular
 * (`singularDoTermo`). Review r15 visual M1: o termo de texto livre só anula o nome quando é o PRIMEIRO (`descricao_conta` é
 * texto; `valor_nota`, `conta_obs` e `saldo_titulo` são dinheiro e conta), e `titulo_eleitoral` é documento.
 */
export function sensivelPorNome(chave: string, opcoes: OpcoesSensivel = {}): TipoSensivel | null {
  const t = termosDaChave(chave);
  if (!t.length) return null;
  const sing = t.map(singularDoTermo);
  const tem = (s: ReadonlySet<string>) => t.some((x, i) => s.has(x) || s.has(sing[i]!));
  const has = (w: string) => t.includes(w) || sing.includes(w);
  const antesDe = (a: string, b: string) => t.some((x, i) => (x === a || sing[i] === a) && (t[i + 1] === b || sing[i + 1] === b));
  const eleitoral = (i: number) => t[i] === 'titulo' && (t[i + 1] === 'eleitor' || t[i + 1] === 'eleitoral');

  // Texto livre: só quando o termo livre é o primeiro (com as exceções de `titulo_eleitor(al)` e `nota_fiscal`).
  if (LIVRE.has(t[0]!) && !eleitoral(0) && !(t[0] === 'nota' && t[1] === 'fiscal')) return null;
  // Review r21 spec (M2): protocolo do WhatsApp (antes do metadado: `wa_id` e `recipient_id` terminam em `id`).
  if (opcoes.whatsapp) {
    if (TELEFONE_WHATSAPP.has(t.join('_'))) return 'telefone';
    if (has('name') && !ULTIMO_META.has(t[t.length - 1]!)) return 'nome';
  }
  // Documento cujo nome termina em `id` (antes da regra do metadado) e quem nasceu em (`nascido_em`, antes do `_em`).
  if (antesDe('tax', 'id') || antesDe('national', 'id')) return 'documento';
  if (has('nascido') || has('nascida')) return 'nascimento';
  // Identificador ou metadado.
  if (ULTIMO_META.has(t[t.length - 1]!) || PRIMEIRO_META.has(t[0]!)) return null;

  // Documentos: o mais específico primeiro.
  if (has('cpf')) return 'cpf';
  if (has('cnpj')) return 'cnpj';
  if (has('rg') || has('identidade') || antesDe('registro', 'geral')) return 'rg';
  if (tem(PIS)) return 'pis';
  if (t.some((_, i) => eleitoral(i)) || has('eleitor') || has('eleitoral')) return 'titulo_eleitor';
  if (has('passaporte') || has('passport') || has('pasaporte')) return 'passaporte';
  // Fase 2 (W5): as inscrições de produtor rural (`inscricoes_produtor`, dados fiscais do parceiro) também.
  if (tem(DOCUMENTO) || antesDe('inscricao', 'estadual') || antesDe('inscricao', 'municipal') || antesDe('inscricao', 'produtor')) return 'documento';

  // Pix antes de conta e de e-mail ("chave_pix_email" é Pix).
  if (has('pix')) return 'pix';
  // `ag` e `cc` só sozinhos ou com número (`num_cc`): curtos demais para valer em qualquer posição.
  const curto = (w: string) => has(w) && (t.length === 1 || tem(NUMERO));
  if (has('agencia') || curto('ag')) return 'agencia';
  if ((has('conta') || has('cuenta')) && !has('contabil') && !has('contable') && !has('plano')) return 'conta';
  if (has('bancario') || has('bancaria') || has('iban') || (has('account') && (has('bank') || has('number'))) || curto('cc')) return 'conta';

  if (has('email') || has('correo') || antesDe('e', 'mail') || t[t.length - 1] === 'mail') return 'email';
  if (tem(TELEFONE)) return 'telefone';
  if (tem(NASCIMENTO) || tem(SEXO)) return 'nascimento';

  // Endereço: cidade, UF e país ficam à vista.
  if (!tem(LOCAL_PUBLICO)) {
    if (tem(CEP) || antesDe('codigo', 'postal')) return 'cep';
    if (tem(BAIRRO)) return 'bairro';
    if (tem(LOGRADOURO)) return 'logradouro';
    if (tem(NUMERO) && (opcoes.emEndereco || tem(CONTEXTO_CASA))) return 'numero_casa';
    if (tem(ENDERECO) || (has('end') && (t.length === 1 || tem(CONTEXTO_END)))) return 'endereco';
  }

  // Dinheiro.
  if (!tem(NAO_DINHEIRO) && !has('centro') && !has('tabela')) {
    if (tem(DINHEIRO) || t[0] === 'total' || antesDe('pro', 'labore')) return 'dinheiro';
  }

  // Nome de pessoa ou empresa.
  if (antesDe('razao', 'social') || antesDe('nome', 'fantasia') || (t.length === 1 && t[0] === 'titular')) return 'nome';
  if (has('nome') && tem(PAPEL_PESSOA)) return 'nome';
  // Review r15 spec M1: o papel sozinho é o nome da pessoa (`mae`, `conjuge`, `socio2`), e `nome_responsavel` (o nome antes) é
  // o nome que o administrador digitou; `responsavel_nome` continua de fora (é o usuário do sistema que o servidor manda).
  const semDigito = t.filter((x) => !/^\d+$/.test(x));
  if (semDigito.length === 1 && PAPEL_SOZINHO.has(semDigito[0]!)) return 'nome';
  if (antesDe('nome', 'responsavel')) return 'nome';
  return null;
}

/** Atalhos de grupo, para quem só precisa saber "some inteiro" ou "é documento". */
export const ehDocumentoPorNome = (tipo: TipoSensivel | null) => !!tipo && TIPOS_DOCUMENTO.has(tipo);
export const ehOcultoPorNome = (tipo: TipoSensivel | null) => !!tipo && TIPOS_OCULTOS.has(tipo);

/** Tipo do campo personalizado (definição em Configurações > Campos personalizados). */
export type TipoExtra = 'texto' | 'numero' | 'data' | 'lista' | 'booleano';

/** O que a regra do campo personalizado precisa da definição: tipo (do servidor ou do formulário), rótulo e a marca `sensivel`. */
export interface DefExtraSensivel {
  tipo?: string | null;
  rotulo?: string | null;
  sensivel?: boolean | null;
}

const ISO_DATA_EXTRA = /^\d{4}-\d{2}-\d{2}$/;

/** Fase 2 (W5, L10): tipo novo de campo personalizado e o tipo sensível que ele sempre é no modo discreto. */
const TIPO_NOVO_SENSIVEL: Readonly<Record<string, TipoSensivel>> = { dinheiro: 'dinheiro', cep: 'cep', documento: 'documento', telefone: 'telefone', email: 'email' };

/** Tipo do campo personalizado pela definição; sem ela (campo removido, definição não carregada), pelo valor gravado. */
export function tipoDoExtra(def: DefExtraSensivel | null | undefined, valor?: unknown): TipoExtra {
  const t = def?.tipo;
  if (t === 'numero' || t === 'data' || t === 'booleano' || t === 'lista') return t;
  if (t === 'selecao') return 'lista';
  if (t === 'datahora') return 'data';
  if (t) return 'texto';
  if (typeof valor === 'number') return 'numero';
  if (typeof valor === 'boolean') return 'booleano';
  if (typeof valor === 'string' && ISO_DATA_EXTRA.test(valor)) return 'data';
  return 'texto';
}

/**
 * Regra EFETIVA do campo personalizado no modo discreto (review r15 spec M1), a mesma na ficha, na Tabela, no Editar e na
 * Auditoria (e no histórico do chatter). A lista de nomes nunca fica completa, então o tipo e a marca da definição decidem:
 *  - número e data: sempre ocultos (`'oculto'`), qualquer que seja o nome (`quantidade_x` também);
 *  - texto e lista: o tipo sensível pelo nome da CHAVE ou do RÓTULO (`sensivelPorNome`; o formatador do tipo mostra parte do
 *    documento, do e-mail e do telefone; dinheiro, endereço e nascimento somem); sem nome sensível, `'oculto'` quando a
 *    definição tem a marca `sensivel`. Nome de pessoa com a marca também some inteiro (a marca vale mais que a opção de nomes);
 *  - booleano: visível (null).
 * Sem definição, o tipo vem do valor gravado (número, data ISO, booleano) e só a chave conta.
 */
export function sensivelDoExtra(chave: string, def: DefExtraSensivel | null | undefined, valor?: unknown): TipoSensivel | 'oculto' | null {
  // Fase 2 (W5, L10): os tipos novos são sempre sensíveis, pelo tipo e não pelo nome. Dinheiro e CEP somem; documento,
  // telefone e e-mail passam pelo formatador do tipo (mostram parte), como os campos fixos.
  const novo = TIPO_NOVO_SENSIVEL[def?.tipo ?? ''];
  if (novo) return novo;
  const tipo = tipoDoExtra(def, valor);
  if (tipo === 'booleano') return null;
  if (tipo === 'numero' || tipo === 'data') return 'oculto';
  const porNome = sensivelPorNome(chave) ?? (def?.rotulo ? sensivelPorNome(def.rotulo) : null);
  if (def?.sensivel && (!porNome || porNome === 'nome')) return 'oculto';
  return porNome;
}
